/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useProfileData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading AI profile data for edit/duplicate modes.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useRef } from 'react';

// Accepts plain integers OR UUID strings (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
const ID_PATTERN = /^(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { getAiProfileById } from '../api/aiProfilesApi';
import type { AiProfile } from '../api/aiProfilesApi';

export const DEFAULT_PROFILE_FORM: Partial<AiProfile> & Pick<AiProfile, 'domain_id' | 'type' | 'model' | 'status'> = {
  domain_id: 1,
  provider_id: undefined,
  type: 'general',
  model: '',
  temperature: 0.7,
  max_tokens: 1000,
  voice_model: '',
  embeddings_model: '',
  timeout_ms: 30000,
  retry_attempts: 3,
  status: true
};

export function useProfileData(id: string | undefined, duplicateId: string | null, resetParam?: string | null) {
  const { showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const [formData, setFormData] = useState<Partial<AiProfile> & Pick<AiProfile, 'domain_id' | 'type' | 'model' | 'status'>>(() => ({ ...DEFAULT_PROFILE_FORM }));
  const [baseline, setBaseline] = useState<Partial<AiProfile> & Pick<AiProfile, 'domain_id' | 'type' | 'model' | 'status'> | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const profileRequestRef = useRef<AbortController | null>(null);
  const profileLoadedRef = useRef<string | null>(null);
  const formResetRef = useRef(false);
  const lastIdRef = useRef<string | undefined>(id);
  const resetParamProcessedRef = useRef<string | null>(null);

  const showInfoRef = useRef(showInfo);
  const showErrorRef = useRef(showError);
  const translateRef = useRef(t);
  const setFormDataRef = useRef(setFormData);
  const setBaselineRef = useRef(setBaseline);

  useEffect(() => {
    showInfoRef.current = showInfo;
    showErrorRef.current = showError;
    translateRef.current = t;
    setFormDataRef.current = setFormData;
    setBaselineRef.current = setBaseline;
  }, [showInfo, showError, t, setFormData, setBaseline]);

  useEffect(() => {
    const loadId = duplicateId || id;
    const idChangedToNew = lastIdRef.current && lastIdRef.current !== 'new' && (id === 'new' || id === undefined);
    const shouldReset = resetParam && resetParam !== resetParamProcessedRef.current;

    // Reset form if navigating to 'new' or resetParam is present
    if ((id === 'new' || id === undefined || idChangedToNew || shouldReset) && !duplicateId) {
      if (shouldReset) {
        resetParamProcessedRef.current = resetParam;
      }
      const fresh = { ...DEFAULT_PROFILE_FORM };
      setFormDataRef.current(fresh);
      setBaselineRef.current(null);
      profileLoadedRef.current = null;
      lastIdRef.current = id;
      setIsLoading(false);
      return;
    }

    // Reset loaded ref when id changes
    if (profileLoadedRef.current !== loadId) {
      profileLoadedRef.current = null;
    }

    lastIdRef.current = id;

    if (loadId && loadId !== 'new') {
      if (!ID_PATTERN.test(loadId)) {
        showErrorRef.current?.(translateRef.current('aiProfiles.messages.loadError'));
        setIsLoading(false);
        return;
      }

      // Prevent loading if already loaded
      if (profileLoadedRef.current === loadId) {
        setIsLoading(false);
        return;
      }

      profileLoadedRef.current = loadId;

      // Fetch profile
      profileRequestRef.current?.abort();
      const controller = new AbortController();
      profileRequestRef.current = controller;

      setIsLoading(true);

      getAiProfileById(loadId)
        .then(response => {
          if (profileRequestRef.current !== controller) return;

          const payload = response.data;
          const isDuplicate = Boolean(duplicateId);

          if (isDuplicate) {
            // For duplicate, remove id and append _copy to profile_name
            const duplicated = {
              ...payload,
              id: undefined,
              uuid: undefined,
              profile_name: payload.profile_name ? `${payload.profile_name}_copy` : ''
            };

            // Ensure voice_model is populated for Azure if it's in the model field
            if (duplicated.provider_type === 'azure' && !duplicated.voice_model && duplicated.model) {
              duplicated.voice_model = duplicated.model;
            }

            setFormDataRef.current(duplicated);
            setBaselineRef.current(null);
            showInfoRef.current?.(translateRef.current('aiProfiles.messages.duplicateHint'));
          } else {
            // Ensure voice_model is populated for Azure if it's in the model field
            if (payload.provider_type === 'azure' && !payload.voice_model && payload.model) {
              payload.voice_model = payload.model;
            }

            setFormDataRef.current(payload);
            // IMPORTANT: Create a deep copy for baseline to prevent shared object mutation
            // When formData is modified, we don't want baseline to change
            setBaselineRef.current(JSON.parse(JSON.stringify(payload)));
          }
          setIsLoading(false);
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((error: any) => {
          if (profileRequestRef.current !== controller) return;

          const isAbort =
            error?.name === 'AbortError' ||
            (error instanceof DOMException && error.name === 'AbortError');

          if (!isAbort) {
            showErrorRef.current?.(translateRef.current('aiProfiles.messages.loadError'));
          }
          setIsLoading(false);
        })
        .finally(() => {
          if (profileRequestRef.current === controller) {
            profileRequestRef.current = null;
          }
        });
    } else {
      // Reset form for new profile
      const fresh = { ...DEFAULT_PROFILE_FORM };
      setFormDataRef.current(fresh);
      setBaselineRef.current(null);
      setIsLoading(false);
    }

    return () => {
      profileRequestRef.current?.abort();
    };
  }, [id, duplicateId, resetParam]);

  return {
    formData,
    setFormData,
    baseline,
    setBaseline,
    isLoading
  };
}

